import { useMemo } from 'react';
import { formatPercent, formatNumber, formatCurrencyCompact, formatPrice, pnlColorClass } from '../../lib/formatters';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateSegmentPackageDemand } from '../../lib/calculations';
import { SEGMENTS, FEATURE_NAMES } from '../../data/conjointData';
import { AnimatedNumber } from '../common/AnimatedNumber';
import { InfoButton } from '../common/Tooltip';
import { Trophy, Check, X } from 'lucide-react';
import type { PackageConfig, ContributionResult, PortfolioPnLResult } from '../../types';

interface ComparisonTableProps {
  packages: PackageConfig[];
  portfolio: PortfolioPnLResult;
  demandMode: 'ceiling' | 'split';
}

interface Row {
  label: string;
  helpKey: string;
  getValue: (c: ContributionResult) => number;
  getPortfolioValue?: (p: PortfolioPnLResult) => number;
  format: (v: number) => string;
  colorFn?: (v: number) => string;
  bestFn: 'max' | 'min';
}

export function ComparisonTable({ packages, portfolio, demandMode }: ComparisonTableProps) {
  const featureScaling = useSimulatorStore(s => s.assumptions.featureScaling);
  const isCeiling = demandMode === 'ceiling';

  const segmentDemands = useMemo(() =>
    packages.map(pkg => calculateSegmentPackageDemand(pkg.price, pkg.enabledFeatures, featureScaling)),
    [packages, featureScaling]
  );

  const DEMAND_ROWS: Row[] = [
    { label: 'Purchase Probability', helpKey: 'adjChoiceRate', getValue: c => c.adjustedChoiceRate, format: v => formatPercent(v), bestFn: 'max' },
    { label: 'Required Close Rate', helpKey: 'impliedCloseRate', getValue: c => c.impliedCloseRate, format: v => formatPercent(v), colorFn: v => v <= 0.28 ? 'text-metric-positive' : v <= 0.35 ? 'text-chart-amber' : 'text-metric-negative', bestFn: 'min' },
    {
      label: 'S2 Units',
      helpKey: 's2Units',
      getValue: c => isCeiling ? c.s2UnitsCeiling : c.s2UnitsSplit,
      getPortfolioValue: p => isCeiling ? p.totalS2UnitsCeiling : p.totalS2UnitsSplit,
      format: v => formatNumber(v),
      bestFn: 'max',
    },
  ];

  const FINANCIAL_ROWS: Row[] = [
    {
      label: 'Contribution Margin',
      helpKey: 'contributionMargin',
      getValue: c => isCeiling ? c.contributionMarginCeiling : c.contributionMarginSplit,
      getPortfolioValue: p => isCeiling ? p.totalContributionCeiling : p.totalContributionSplit,
      format: v => formatCurrencyCompact(v),
      colorFn: v => pnlColorClass(v),
      bestFn: 'max',
    },
    {
      label: 'Net Revenue',
      helpKey: 'netRevenue',
      getValue: c => isCeiling ? c.netRevenueCeiling : c.netRevenueSplit,
      getPortfolioValue: p => isCeiling ? p.totalNetRevenueCeiling : p.totalNetRevenueSplit,
      format: v => formatCurrencyCompact(v),
      bestFn: 'max',
    },
    {
      label: 'Net Income / Unit',
      helpKey: 'netIncomePerUnit',
      getValue: c => c.netIncomePerUnit,
      format: v => formatPrice(Math.round(v)),
      colorFn: v => pnlColorClass(v),
      bestFn: 'max',
    },
  ];

  const contributions = portfolio.packages;

  function renderRow(row: Row) {
    const values = contributions.map(row.getValue);
    const finite = values.filter(v => isFinite(v));
    const best = row.bestFn === 'max' ? Math.max(...finite) : Math.min(...finite);
    const bestIdx = finite.length > 0 ? values.indexOf(best) : -1;
    const portfolioVal = row.getPortfolioValue?.(portfolio);

    return (
      <tr key={row.label} className="border-b border-border/50 transition-colors"
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
        <td className="py-2.5 px-3 text-secondary">
          <span className="flex items-center gap-1.5">{row.label}<InfoButton helpKey={row.helpKey} size={11} /></span>
        </td>
        {values.map((v, i) => (
          <td key={i} className="text-right py-2.5 px-3">
            <AnimatedNumber value={isFinite(v) ? v : 0} formatter={row.format} className={`font-mono ${row.colorFn ? row.colorFn(v) : i === bestIdx ? 'text-chart-teal' : 'text-primary'}`} />
          </td>
        ))}
        <td className="text-right py-2.5 px-3 font-mono font-semibold">
          {portfolioVal !== undefined ? (
            <AnimatedNumber value={portfolioVal} formatter={row.format} className={row.colorFn ? row.colorFn(portfolioVal) : 'text-primary'} />
          ) : ''}
        </td>
        <td className="text-center py-2.5 px-3">
          {bestIdx >= 0 && <span className="inline-flex items-center gap-1 text-chart-teal text-xs"><Trophy size={12} />{String.fromCharCode(65 + bestIdx)}</span>}
        </td>
      </tr>
    );
  }

  const portfolioPnl = isCeiling ? portfolio.portfolioPnLCeiling : portfolio.portfolioPnLSplit;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Head-to-Head Comparison</h3>
        <InfoButton helpKey="comparisonTable" size={13} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-secondary font-medium">Metric</th>
              {packages.map((pkg, i) => (
                <th key={i} className="text-right py-2 px-3 text-primary font-medium">{pkg.name}</th>
              ))}
              <th className="text-right py-2 px-3 text-chart-teal font-medium">Portfolio</th>
              <th className="text-center py-2 px-3 text-secondary font-medium w-16">Best</th>
            </tr>
          </thead>
          <tbody>
            {/* Features included row */}
            <tr className="border-b border-border/50">
              <td className="py-2.5 px-3 text-secondary">Features Included</td>
              {packages.map((pkg, i) => (
                <td key={i} className="py-2.5 px-3 text-right">
                  <div className="flex flex-wrap gap-1 justify-end">
                    {FEATURE_NAMES.map(f => (
                      <span key={f} className={`w-5 h-5 rounded text-[8px] flex items-center justify-center ${pkg.enabledFeatures.includes(f) ? 'bg-chart-teal/20 text-chart-teal' : ''}`}
                        style={!pkg.enabledFeatures.includes(f) ? { backgroundColor: 'var(--color-feature-off-bg)', color: 'var(--color-feature-off-text)' } : undefined}
                        title={f}
                      >
                        {pkg.enabledFeatures.includes(f) ? <Check size={8} /> : <X size={8} />}
                      </span>
                    ))}
                    <span className="text-xs text-muted font-mono ml-1">{pkg.enabledFeatures.length}/7</span>
                  </div>
                </td>
              ))}
              <td className="text-right py-2.5 px-3" />
              <td className="text-center py-2.5 px-3" />
            </tr>

            {/* Demand metrics */}
            {DEMAND_ROWS.map(row => renderRow(row))}

            {/* Segment demand rows */}
            <tr className="border-b border-border">
              <td colSpan={packages.length + 3} className="py-1.5 px-3">
                <span className="inline-flex items-center gap-1.5 text-micro text-muted uppercase tracking-wider">Segment Demand (scenario) <InfoButton helpKey="segmentDemandScenario" size={10} /></span>
              </td>
            </tr>
            {SEGMENTS.map((seg, si) => (
              <tr key={seg.name} className="border-b border-border/30 transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
                <td className="py-2 px-3 text-secondary text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                    {seg.shortName}
                  </span>
                </td>
                {segmentDemands.map((pkgSegs, pi) => (
                  <td key={pi} className="text-right py-2 px-3 font-mono text-xs text-primary">
                    {formatPercent(pkgSegs[si].segmentRate)}
                  </td>
                ))}
                <td className="text-right py-2 px-3" />
                <td className="text-center py-2 px-3" />
              </tr>
            ))}
            <tr className="border-b border-border/30">
              <td colSpan={packages.length + 3} className="py-0.5 px-3">
                <span className="text-micro text-muted italic">Feature lifts applied uniformly across segments.</span>
              </td>
            </tr>

            {/* Financial metrics */}
            <tr className="border-b border-border">
              <td colSpan={packages.length + 3} className="py-1.5 px-3">
                <span className="inline-flex items-center gap-1.5 text-micro text-muted uppercase tracking-wider">Portfolio Economics <InfoButton helpKey="scenarioEconomics" size={10} /></span>
              </td>
            </tr>
            {FINANCIAL_ROWS.map(row => renderRow(row))}

            {/* Portfolio P&L summary row */}
            <tr className="border-t-2 border-border transition-colors"
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
              <td className="py-3 px-3 text-primary font-semibold">
                <span className="flex items-center gap-1.5">Portfolio P&L<InfoButton helpKey="portfolioPnL" size={11} /></span>
              </td>
              <td colSpan={packages.length} className="py-3 px-3 text-right text-xs text-muted">
                Sum of contributions − {formatCurrencyCompact(portfolio.fixedCosts)} fixed costs
              </td>
              <td className="text-right py-3 px-3">
                <AnimatedNumber value={portfolioPnl} formatter={v => formatCurrencyCompact(v)} className={`font-mono font-bold text-base ${pnlColorClass(portfolioPnl)}`} />
              </td>
              <td className="text-center py-3 px-3" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
